import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaCalendar, FaMapMarkerAlt, FaUsers, FaExternalLinkAlt, FaTimes, FaChevronLeft, FaChevronRight, FaEye } from 'react-icons/fa';
import { achievements } from '../data/portfolioData';
import AnimatedSection from './AnimatedSection';
import { useTranslation } from 'react-i18next';
import { localizedItem } from '../locales/contentTranslations';
import useCardSlider from "../hooks/useCardSlider";

const AchievementsSlider = () => {
  const { t, i18n } = useTranslation();
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  
  const {
    startIndex,
    setStartIndex,
    visibleCount,
    next: nextSlide,
    prev: prevSlide,
    sliderProps
  } = useCardSlider(
    achievements.length,
    selectedAchievement !== null,
    10000,
    true
  );

  const itemsPerPage = visibleCount;
  const totalPages = Math.ceil(achievements.length / visibleCount);
  const currentPage = Math.floor(startIndex / visibleCount);

  const localizedAchievements = achievements.map(item =>
    localizedItem('achievements', item, i18n)
  );

  const visibleAchievements = localizedAchievements.slice(
    startIndex,
    startIndex + visibleCount
  );

const currentAchievement = selectedAchievement
  ? localizedAchievements.find(
      (item) => item.id === selectedAchievement.id
    )
  : null;


  const handleImageError = (achievementId, imgIndex) => {
    setImageErrors(prev => ({ ...prev, [`${achievementId}-${imgIndex}`]: true }));
  };


  const openModal = (achievement) => {
    setSelectedAchievement(achievement);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedAchievement(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    if (selectedAchievement && currentAchievement.images) {
      setCurrentImageIndex((prev) => (prev + 1) % currentAchievement.images.length);
    }
  };

  const prevImage = () => {
    if (selectedAchievement && currentAchievement.images) {
      setCurrentImageIndex((prev) => (prev - 1 + currentAchievement.images.length) % currentAchievement.images.length);
    }
  };

  return (
    <section id="achievements" className="py-16 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex items-center gap-3 justify-center mb-4">
            <FaTrophy className="text-primary text-3xl md:text-4xl" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center">
              {t("achievements.title")}
            </h2>
          </div>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12 text-sm md:text-base">
            {t("achievements.subtitle")}
          </p>
        </AnimatedSection>

        {/* Slajder */}
        <div className="relative" {...sliderProps}>
          {/* Navigation dugmad */}
          <button
            onClick={prevSlide}
            className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition hover-glow"
          >
            <FaChevronLeft size={20} />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition hover-glow"
          >
            <FaChevronRight size={20} />
          </button>

          {/* Grid sa 3 kartice */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="wait">
              {visibleAchievements.map((achievement, index) => (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="group cursor-pointer h-full"
                  onClick={() => openModal(achievement)}
                >
                  <div className="relative glass-effect rounded-2xl overflow-hidden h-full transition-all duration-300 hover-glow">
                    {/* Thumbnail slika */}
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20">
                      {achievement.thumbnail && !imageErrors[`${achievement.id}-thumb`] ? (
                        <img
                          src={achievement.thumbnail}
                          alt={achievement.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={() => handleImageError(achievement.id, 'thumb')}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-6xl">
                          {achievement.icon}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent opacity-60"></div>
                      
                      {/* Badge za godinu */}
                      <div
                      className={`absolute top-3 right-3 px-2 py-1 rounded-lg text-xs font-semibold ${
                        parseInt(achievement.year, 10) === 2026
                          ? "bg-pink-500 text-white"
                          : "glass-effect text-white"
                      }`}
                    >
                      {achievement.year}
                    </div>
                    </div>
                    
                    <div className="p-5">
                      <h3 className="text-lg md:text-xl font-bold group-hover:text-primary transition line-clamp-2 mb-1">
                        {achievement.title}
                      </h3>
                      <p className="text-primary text-sm mb-2">{achievement.organization}</p>
                      
                      <div className="flex items-center gap-2 text-gray-400 text-xs mb-3">
                        {achievement.location && (
                          <>
                            <FaMapMarkerAlt size={10} />
                            <span className="truncate">{achievement.location}</span>
                          </>
                        )}
                        {achievement.teamSize && (
                          <>
                            <span>•</span>
                            <FaUsers size={10} />
                            <span>{achievement.teamSize}</span>
                          </>
                        )}
                      </div>
                      
                      <p className="text-gray-300 text-sm line-clamp-2">
                        {achievement.description}
                      </p>
                      
                      <div className="mt-4 flex items-center gap-2 text-primary text-sm group-hover:gap-3 transition-all">
                        <FaEye size={12} />
                        <span>{t('achievements.viewDetails')}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Paginacija / Indikatori */}
          <div className="flex justify-center gap-1 mt-3">
            {Array.from({ length: totalPages }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setStartIndex(idx)}
                className={`transition-all ${
                  currentPage === idx 
                    ? 'w-3 h-0.5 bg-primary rounded-full' 
                    : 'w-1 h-0.5 bg-gray-600/40 rounded-full'
                }`}
              />
            ))}
          </div>

          {/* Brojač */}
          <div className="text-center mt-4">
            <p className="text-gray-500 text-sm">
              {t('achievements.showing')} {startIndex + 1} - {Math.min(startIndex + itemsPerPage, achievements.length)} {t('achievements.of')} {achievements.length} {t('achievements.items')}
            </p>
          </div>
        </div>
      </div>

      {/* Modal - isti kao prije */}
      <AnimatePresence>
        {currentAchievement  && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              className="relative bg-gradient-to-br from-darker to-dark rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-20 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                <FaTimes />
              </button>

              {/* Gallery */}
              {currentAchievement.images && currentAchievement.images.length > 0 && (
                <div className="relative">
                  <div className="relative h-[50vh] min-h-[300px] max-h-[500px] bg-black/50 flex items-center justify-center">
                    {!imageErrors[`${currentAchievement.id}-${currentImageIndex}`] ? (
                      <img
                        src={currentAchievement.images[currentImageIndex]}
                        alt={`${currentAchievement.title} - slika ${currentImageIndex + 1}`}
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        onError={() => handleImageError(currentAchievement.id, currentImageIndex)}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-4">
                        <span className="text-8xl">{currentAchievement.icon}</span>
                        <p className="text-gray-400">{t('achievements.imageUnavailable')}</p>
                      </div>
                    )}
                    
                    {currentAchievement.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
                        >
                          <FaChevronLeft />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
                        >
                          <FaChevronRight />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {currentAchievement.images.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {currentAchievement.images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentImageIndex(i)}
                          className={`w-1 h-1 rounded-full ${
                            i === currentImageIndex ? 'bg-primary' : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-5xl">{currentAchievement.icon}</span>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold">{currentAchievement.title}</h3>
                    <p className="text-primary text-base">{currentAchievement.organization}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <FaCalendar />
                    <span>{currentAchievement.year}</span>
                  </div>
                  {currentAchievement.location && (
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt />
                      <span>{currentAchievement.location}</span>
                    </div>
                  )}
                  {currentAchievement.teamSize && (
                    <div className="flex items-center gap-2">
                      <FaUsers />
                      <span>{currentAchievement.teamSize}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-gray-300 leading-relaxed mb-6">
                  {currentAchievement.description}
                </p>
                
                {currentAchievement.link && currentAchievement.link !== '#' && (
                  <a
                    href={currentAchievement.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-primary to-secondary rounded-lg text-white font-semibold hover-glow transition"
                  >
                    {t('achievements.learnMore')}<FaExternalLinkAlt size={14} />
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default AchievementsSlider;